import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ManagerApproval, approvalRefusal, isOverridable } from '../auth/manager-approval.js';
import type { ManagerOverride } from '../auth/manager-approval.js';
import { useNotice } from '../../components/notice.js';
import { Button, Callout, Modal } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';

/**
 * One person, one record (FB-1104).
 *
 * Whoever already has the email or phone being typed is shown before a second
 * record is made — on the register's quick add, the Customers screen's new
 * customer, and an edit. Email is what a web order is matched on, so the
 * server refuses a second one until the person says this is someone else;
 * a shared phone is only pointed out, because a family shares a landline.
 */

export interface Duplicate {
  readonly id: string;
  readonly name: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly sharesEmail: boolean;
  readonly sharesPhone: boolean;
}

/** Who already has this email or phone, asked once typing pauses. */
export function useDuplicates(
  email: string,
  phone: string,
  excludeId: string | null = null,
): { duplicates: readonly Duplicate[]; sharesEmail: boolean } {
  const [settled, setSettled] = useState({ email: email.trim(), phone: phone.trim() });
  useEffect(() => {
    const timer = setTimeout(() => setSettled({ email: email.trim(), phone: phone.trim() }), 400);
    return () => clearTimeout(timer);
  }, [email, phone]);
  const asked =
    settled.email.includes('@') || settled.phone.replace(/\D/g, '').length >= 7 ? settled : null;
  const found = useQuery({
    queryKey: ['customers', 'duplicatesOf', asked?.email ?? '', asked?.phone ?? '', excludeId],
    enabled: asked !== null,
    queryFn: () =>
      api.customers.duplicatesOf.query({
        email: asked!.email.includes('@') ? asked!.email : null,
        phone: asked!.phone === '' ? null : asked!.phone,
        excludeId,
      }),
  });
  const duplicates = asked === null ? [] : (found.data ?? []);
  return { duplicates, sharesEmail: duplicates.some((entry) => entry.sharesEmail) };
}

function shared(entry: Duplicate): string {
  return entry.sharesEmail && entry.sharesPhone
    ? 'the same email and phone'
    : entry.sharesEmail
      ? 'the same email'
      : 'the same phone';
}

/** The records already there, each with a way to use it instead. */
export function DuplicateNotice({
  duplicates,
  useLabel,
  onUse,
}: {
  duplicates: readonly Duplicate[];
  /** "Use them", "Attach them" — what choosing the existing record does here. */
  useLabel?: string;
  onUse?: (customerId: string) => void;
}) {
  if (duplicates.length === 0) return null;
  return (
    <Callout tone="warning" title="Already a customer?">
      <ul className="duplicates">
        {duplicates.map((entry) => (
          <li key={entry.id} className="duplicates__row">
            <span>
              <strong>{entry.name}</strong> has {shared(entry)}
              {entry.email !== null && entry.sharesEmail ? ` (${entry.email})` : ''}.
            </span>
            {onUse !== undefined ? (
              <Button variant="secondary" onClick={() => onUse(entry.id)}>
                {useLabel ?? 'Use them'}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </Callout>
  );
}

/**
 * Put another record of this person into this one (FB-1104).
 *
 * The open record is the one kept — open the other to keep that one instead.
 * Everything the other owns moves here; it is retired, not deleted, with a
 * note saying where it went. A manager approves it, because store credit and
 * gift cards move with it.
 */
export function MergeCustomerDialog({
  customerId,
  customerName,
  candidate = null,
  onClose,
}: {
  customerId: string;
  customerName: string;
  /** Proposed by a duplicate notice; otherwise found by searching. */
  candidate?: { id: string; name: string } | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const [term, setTerm] = useState('');
  const [chosen, setChosen] = useState(candidate);
  const [needsApproval, setNeedsApproval] = useState(false);

  const results = useQuery({
    queryKey: ['customers', 'search', term.trim()],
    enabled: chosen === null && term.trim().length >= 2,
    queryFn: () => api.customers.search.query({ term: term.trim() }),
  });

  const merge = useMutation({
    mutationFn: (override?: ManagerOverride) =>
      api.customers.merge.mutate({
        keepId: customerId,
        mergeId: chosen!.id,
        ...(override === undefined ? {} : { override }),
      }),
    onSuccess: () => {
      recordBreadcrumb('customers', 'merged two records of one customer');
      if (chosen !== null) notify({ title: `Merged — ${chosen.name} into ${customerName}` });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      onClose();
    },
    onError: (error) => {
      if (isOverridable(error)) setNeedsApproval(true);
    },
  });

  if (needsApproval) {
    return (
      <ManagerApproval
        title="Manager approval needed"
        because="Merging moves this person's store credit, gift cards and history from one record to the other."
        busy={merge.isPending}
        error={merge.isError ? approvalRefusal(merge.error) : null}
        onCancel={onClose}
        onApprove={(override) => merge.mutate(override)}
      />
    );
  }

  const others = (results.data ?? []).filter((entry) => entry.id !== customerId);

  return (
    <Modal
      open
      title="Merge a duplicate record"
      description={`Everything on the other record — sales, layaways, gift cards, store credit, repairs, consignments and web orders — moves to ${customerName}. The other record is retired with a note saying where it went.`}
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={merge.isPending}
          disabledReason={chosen === null ? 'Choose the record to merge in' : undefined}
          onClick={() => merge.mutate(undefined)}
        >
          {chosen === null ? 'Merge' : `Merge ${chosen.name} into ${customerName}`}
        </Button>
      }
    >
      {chosen === null ? (
        <>
          <div className="field">
            <label className="field__label" htmlFor="merge-search">
              Find the other record
            </label>
            <input
              id="merge-search"
              className="input"
              autoFocus
              autoComplete="off"
              placeholder="Name, phone or email"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
            />
          </div>
          <ul className="duplicates">
            {others.map((entry) => (
              <li key={entry.id} className="duplicates__row">
                <span>
                  <strong>{entry.name ?? 'Unnamed customer'}</strong>
                  {[entry.phone, entry.email].filter((part) => part !== null).length > 0
                    ? ` · ${[entry.phone, entry.email].filter((part) => part !== null).join(' · ')}`
                    : ''}
                </span>
                <Button
                  variant="secondary"
                  onClick={() =>
                    setChosen({ id: entry.id, name: entry.name ?? 'Unnamed customer' })
                  }
                >
                  Choose
                </Button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p>
          <strong>{chosen.name}</strong> will be merged into <strong>{customerName}</strong>.
          Details only {chosen.name} has fill the gaps; where both have one, {customerName}&rsquo;s
          stays. Tax exemption does not move.{' '}
          <Button variant="ghost" onClick={() => setChosen(null)}>
            Choose another
          </Button>
        </p>
      )}
      {merge.isError && !needsApproval ? (
        <Callout tone="danger">{errorMessage(merge.error)}</Callout>
      ) : null}
    </Modal>
  );
}
